<template>
    <div id="prospus-client-auth">
        <PermissionError v-if="show_permission_error_page"></PermissionError>
        <ExpiredPasswordLink
            v-else-if="showResetPassword & hasResetPasswordExpired"
        ></ExpiredPasswordLink>
        <template v-else>
            <AuthLayoutWrapper>
                <Auth
                    :show-sign-up="showSignUp"
                    :show-reset-pwd="showResetPassword"
                    :invitation-code="invitation_code"
                    :signup-email="signupEmail"
                    :invitation-link-invalid="invitationLinkInvalid"
                    @update:show-reset-pwd="showResetPassword = $event"
                    @sign-up="onShowSignUp"
                    @sign-in="onShowSignIn"
                />
            </AuthLayoutWrapper>
        </template>

        <!-- Invitation Error Dialog -->
        <KDialog
            v-model="showInvitationErrorDialog"
            title="Sorry"
            :subtitle="invitationErrorDialogSubtitle"
            confirm-label="OKAY"
            :face-logo="staticAssets.profile"
            :cancel-label="''"
            @onConfirm="closeInvitationErrorDialog"
        />

        <!-- Snackbar Components -->
        <Snackbar />
        <Snackbars />
    </div>
</template>

<script>
import AuthPanel from "./Modules/Auth/AuthPanel.vue";
import AuthDashBoard from "./Modules/Auth/AuthDashBoard.vue";
import PermissionError from "./Modules/Auth/PermissionError.vue";
import ExpiredPasswordLink from "./Modules/Auth/ExpiredPasswordLink.vue";
import Auth from "js/Auth/js/Pages/Auth/AuthIndex.vue";
import AuthLayoutWrapper from "js/Auth/js/Modules/Components/Auth/AuthLayoutWrapper.vue";
import KDialog from "./Modules/Components/Common/KDialog.vue";
import Snackbar from "./Modules/Components/Common/Snackbar.vue";
import Snackbars from "./Modules/Components/Common/Snackbars.vue";
import { useStaticAssets } from "../../Admin/js/Stores/StaticAssets";
import axios from "axios";

export default {
    components: {
        PermissionError,
        AuthPanel,
        AuthDashBoard,
        ExpiredPasswordLink,
        Auth,
        AuthLayoutWrapper,
        KDialog,
        Snackbar,
        Snackbars,
    },
    data() {
        return {
            showSignUp: false,
            showResetPassword: false,
            show_permission_error_page: false,
            hasResetPasswordExpired: false,
            showInvitationErrorDialog: false,
            invitationErrorDialogSubtitle: "",
            invitationLinkInvalid: false,
            signupEmail: null,
        };
    },
    setup() {
        const staticAssets = useStaticAssets();
        return {
            staticAssets,
        };
    },
    computed: {
        isAuthenticated() {
            let token = localStorage.getItem("kaamfu_user_token");

            return token && token.length > 0;
        },
        invitation_code() {
            const queryString = window.location.search;
            const urlParams = new URLSearchParams(queryString);
            const invitationCode = urlParams.get("invitation-code");
            return invitationCode;
        },
    },

    mounted() {
        const params = new URL(location).searchParams;
        if (params.get("action") == "reset_password") {
            this.showResetPassword = true;

            this.hasResetPasswordExpired = Boolean(
                Number(params.get("token_expired")),
            );
        }
        this.show_permission_error_page = this.hasPath();

        // Extract and decode email from query parameter
        this.extractEmailFromUrl();

        // Check if user is on /signup path and show signup
        if (this.invitation_code || window.location.pathname === "/signup") {
            this.showSignUp = true;
        }

        // Check for invitation URL parameters
        this.parseInvitationUrl();

        // Validate signup invitation links before showing the form
        this.validateInvitationLink();

        // Listen for browser navigation events
        window.addEventListener("popstate", this.handleUrlChange);
    },

    beforeUnmount() {
        window.removeEventListener("popstate", this.handleUrlChange);
    },

    methods: {
        onShowSignIn() {
            this.showSignUp = false;
            // Update URL to root / when switching to signin
            window.history.pushState({}, "", "/");
        },
        onShowSignUp() {
            this.showSignUp = true;
            // Update URL to /signup when switching to signup
            window.history.pushState({}, "", "/signup");
        },

        hasPath() {
            const parsedURL = new URL(location.href);
            const pathname = parsedURL.pathname;

            // Handle signup path specially
            if (pathname === "/signup") {
                return false; // Don't show permission error for signup
            }

            return pathname !== "/" && pathname.length === 9;
        },

        parseInvitationUrl() {
            const currentUrl = window.location.href;
            const url = new URL(currentUrl);
            const queryParams = Object.fromEntries(url.searchParams.entries());

            // Check if both token and action parameters are present (invitation approval/rejection)
            if (
                queryParams.token &&
                queryParams.action &&
                queryParams.action !== "reset_password"
            ) {
                // Store the full invitation URL for redirect after sign-in
                const redirectPath = `${url.pathname}${url.search}`;
                sessionStorage.setItem("auth_redirect_url", redirectPath);

                // Show error dialog for unauthenticated users
                this.invitationErrorDialogSubtitle = "You need to be signed in to Kaamfu, to approve or reject this request to associate. Kindly sign in, and then click on the approve (or reject) button in the email you received.";
                this.showInvitationErrorDialog = true;

                // Clear URL parameters to clean up the URL
                const cleanUrl = `${url.origin}${url.pathname}`;
                window.history.replaceState({}, "", cleanUrl);
            }
        },

        extractEmailFromUrl() {
            const queryString = window.location.search;
            const urlParams = new URLSearchParams(queryString);
            const encodedEmail = urlParams.get("e");

            if (!encodedEmail) {
                return;
            }

            try {
                // Base64 decode the email
                const decodedEmail = atob(encodedEmail);

                // Validate that decoded value is a valid email format
                const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                if (emailRegex.test(decodedEmail)) {
                    // Store in data property
                    this.signupEmail = decodedEmail;
                }
            } catch (error) {
                // Handle invalid base64 strings gracefully
                console.warn("Failed to decode email parameter:", error);
            }
        },

        async validateInvitationLink() {
            if (!this.invitation_code) {
                return;
            }

            try {
                const { data } = await axios.get("/v1/invitations/validate", {
                    params: { code: this.invitation_code },
                });

                if (data.data?.status !== "valid") {
                    this.invitationLinkInvalid = true;
                    this.invitationErrorDialogSubtitle = data.data?.message || "This invitation is no longer valid.";
                    this.showInvitationErrorDialog = true;
                }
            } catch (error) {
                console.warn("Failed to validate invitation link", error);
            }
        },

        closeInvitationErrorDialog() {
            this.showInvitationErrorDialog = false;

            if (this.invitationLinkInvalid) {
                this.onShowSignIn();
            }
        },

        handleUrlChange() {
            // Update signup state based on current path
            if (window.location.pathname === "/signup") {
                this.showSignUp = true;
            } else {
                this.showSignUp = false;
            }

            // Update permission error page state
            this.show_permission_error_page = this.hasPath();
        },
    },
};
</script>

<style lang="scss">
@import url("https://fonts.googleapis.com/css2?family=Poppins:ital,wght@0,100;0,200;0,300;0,400;0,500;0,600;0,700;0,800;0,900;1,100;1,200;1,300;1,400;1,500;1,600;1,700;1,800;1,900&family=Roboto:ital,wght@0,100..900;1,100..900&display=swap");
</style>
